import { Label, ComboBox, Input } from '@components/common/Forms';
import { GenerateFields } from '@components/common/Forms/GenerateFields/GenerateFields';
import { CommonFilterForm } from '@type/filters';
import { studyLevels } from '@components/features/filters/helpers/filterObjects';
import { useTranslation } from 'next-i18next';
import { useState, FocusEvent } from 'react';


// Interface for specialization autocomplete
// ************************************
interface StudySpecializationProps {
  id: string,
  text: string,
}


/**
 * @description Generates the fields for the study levels filter group.
 * * **recordFunc** - the 'record' function from useBjForm.
 * * **submitFunc** - the function to trigger the form submit. You might want to trigger it on blur. Defaults to undefined.
 * * **verticalSpacing** - the spacing between the items. Defaults to 'my-1.5'.
 * * **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 */
export const StudyLevelsForm = (props: CommonFilterForm) => {
  // Destructure props
  const {
    recordFunc,
    submitFunc,
    verticalSpacing = 'gap-y-2',
    idPrefix,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // States for the start/end years
  // The user should not be able to select a start year that is greater than the current or end year
  // The user should not be able to select an end year that is smaller than the start year
  const [currentYear] = useState<number>(new Date().getFullYear());
  const [startYear, setStartYear] = useState<number | undefined>();
  const [endYear, setEndYear] = useState<number | undefined>();


  // Helper functions
  // **********************************************
  const handleStartYear = (e: FocusEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (value < currentYear - 65 || value > (endYear || currentYear)) {
      setStartYear(undefined);
    } else {
      setStartYear(value);
    }
  };

  const handleEndYear = (e: FocusEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (value < (startYear || currentYear - 65) || value > currentYear) {
      setEndYear(undefined);
    } else {
      setEndYear(value);
    }
  };


  // Render component
  // **********************************************
  return (
    <div className={`grid ${verticalSpacing}`}>
      {/* Study levels checkboxes */}
      <GenerateFields
        recordFunc={recordFunc}
        submitFunc={submitFunc}
        filterObject={studyLevels}
        spacing="gap-x-0.5"
        idPrefix={idPrefix}
      />

      {/* Specialization combobox */}
      <Label
        description={t('study.faculty.label')}
        descriptionClass="font-bold !mb-2 text-ink-medium"
        as="div"
      >
        <ComboBox
          minLength={1}
          options={{
            freeSolo: false,
            valueFromKey: 'text',
            apiList: {
              fetchUrl: '/v1/static-lists/study-specialization/autocomplete',
              queryParams: (query: string) => ({ q: query }),
            },
          }}
          renderList={(item, index, listKey, getItemProps, listStyling) => {
            // add a type to make TS behave nicely; we already checked for errors object inside Combobox fetch
            const specialization = item as StudySpecializationProps;
            return (
              <li key={listKey} className={listStyling} {...getItemProps({ item, index })}>
                {specialization.text}
              </li>
            );
          }}
          {...(submitFunc ? { onBlur: submitFunc } : {})}
          {...recordFunc('studySpecialization', {
            id: `${idPrefix || ''}studySpecialization`,
          })}
        />
      </Label>

      {/* Studies start/end */}
      <Label
        htmlFor={`${idPrefix || ''}graduateStart`}
        description={t('study.interval.label')}
        descriptionClass="font-bold !mb-2 text-ink-medium"
      >
        {/* start date field */}
        <div>
          <Input
            type="number"
            min={currentYear - 65}
            max={endYear || currentYear}
            step="1"
            placeholder={t('filters.studies.studies-start')}
            className="peer"
            onBlur={(e) => {
              handleStartYear(e);
              if (submitFunc) submitFunc();
            }}
            onInput={(e) => {
              // get the value if set from query params or programmatically
              if (!e.isTrusted) setStartYear(Number(e.currentTarget.value));
            }}
            data-filters-label={`{{filters.studies.studies-start}}: ${startYear || ''}`}
            {...recordFunc('graduateStart', {
              id: `${idPrefix || ''}graduateStart`,
              convertValue: 'toNumber',
            })}
          />
          {/* start date error */}
          <span className="mt-2 hidden text-sm text-error peer-invalid:block">
            {t('date.year.start-end-interval.error', { min: currentYear - 65, max: endYear || currentYear })}
          </span>
        </div>

        {/* end date field */}
        <div className="mt-2.5">
          <Input
            type="number"
            min={startYear || currentYear - 65}
            max={currentYear}
            step="1"
            placeholder={t('filters.studies.studies-end')}
            className="peer"
            onBlur={(e) => {
              handleEndYear(e);
              if (submitFunc) submitFunc();
            }}
            onInput={(e) => {
              // get the value if set from query params or programmatically
              if (!e.isTrusted) setEndYear(Number(e.currentTarget.value));
            }}
            data-filters-label={`{{filters.studies.studies-end}}: ${endYear || ''}`}
            {...recordFunc('graduateEnd', {
              id: `${idPrefix || ''}graduateEnd`,
              convertValue: 'toNumber',
              filtersLabel: 'global.label.date.to',
            })}
          />
          {/* end date error */}
          <span className="mt-2 hidden text-sm text-error peer-invalid:block">
            {t('date.year.start-end-interval.error', { min: startYear || currentYear - 65, max: currentYear })}
          </span>
        </div>
      </Label>
    </div>
  );
};
